import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { EXPLAIN_ENDPOINT } from "./constants";

// AI explanation card. Presentation-only: it POSTs the whitelisted
// scoring inputs to /api/analyzer/explain-score and renders whatever
// the backend returns (or a deterministic fallback). It NEVER modifies
// the PA Score and NEVER invents fields. Preserved verbatim from the
// original PaExplanationBlock.
export function PaExplanationCard({ leg }) {
  const paScore = leg?.pa_score;
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!paScore) return;
    let cancelled = false;
    setState({ status: "loading" });

    const cm = leg.current_market || {};
    const hist = leg.historical || {};
    const payload = {
      player_name: leg.player_name,
      market: leg.market,
      side: leg.side,
      line: leg.user_line,
      user_odds: leg.user_odds,
      pa_score: paScore.pa_score,
      pa_label: paScore.pa_label,
      historical_value_score: paScore.historical_value_score,
      recent_form_score: paScore.recent_form_score,
      market_price_score: paScore.market_price_score,
      confidence: paScore.confidence,
      insufficient_data: paScore.insufficient_data,
      l5_hit_rate: hist.last_5_hit_rate ?? null,
      l10_hit_rate: hist.last_10_hit_rate ?? null,
      season_hit_rate: hist.season_hit_rate ?? null,
      season_average: hist.season_average ?? null,
      l5_pushes: hist.last_5_pushes ?? null,
      l10_pushes: hist.last_10_pushes ?? null,
      season_pushes: hist.season_pushes ?? null,
      games_sampled: hist.games_available ?? null,
      current_market_line: cm.line ?? null,
      best_current_odds: cm.best_price ?? null,
      best_sportsbook: cm.best_book ?? null,
      market_comparison_available:
        paScore.market_price_score !== null &&
        paScore.market_price_score !== undefined,
      market_data_stale: cm.market_data_stale ?? false,
    };

    (async () => {
      try {
        const res = await fetch(EXPLAIN_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          if (cancelled) return;
          setState({
            status: "unavailable",
            explanation:
              "PA's explanation is temporarily unavailable. Your verified score and data are still shown above.",
          });
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setState({
          status: body.reason || "ready",
          explanation: body.explanation || "",
        });
      } catch {
        if (cancelled) return;
        setState({
          status: "unavailable",
          explanation:
            "PA's explanation is temporarily unavailable. Your verified score and data are still shown above.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leg]);

  if (!paScore) return null;

  return (
    <section
      data-testid="analyzer-pa-explanation"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          Why PA Scored It This Way
        </h3>
        <p
          className="text-[11px] uppercase tracking-wider text-slate-500 font-display"
          data-testid="analyzer-pa-explanation-label"
        >
          AI explanation of verified PA data
        </p>
      </div>

      {state.status === "loading" ? (
        <div
          className="mt-4 flex items-center gap-2 text-sm text-slate-600"
          data-testid="analyzer-pa-explanation-loading"
        >
          <Loader2 size={16} className="animate-spin" style={{ color: "#059669" }} />
          Writing your explanation…
        </div>
      ) : (
        <p
          className="mt-4 text-slate-800 text-sm md:text-base leading-relaxed"
          data-testid="analyzer-pa-explanation-body"
        >
          {state.explanation}
        </p>
      )}

      {state.status === "unavailable" || state.status === "provider_unavailable" ? (
        <p
          className="mt-3 text-[11px] text-slate-500"
          data-testid="analyzer-pa-explanation-fallback"
        >
          Fallback copy — the AI writer is temporarily offline.
        </p>
      ) : null}

      {state.status === "safety_guard" ? (
        <p
          className="mt-3 text-[11px] text-slate-500"
          data-testid="analyzer-pa-explanation-safety"
        >
          This explanation was replaced with a safer summary.
        </p>
      ) : null}
    </section>
  );
}
